/**
 * Redux state for organization-specific query reasons from Firestore.
 *
 * @module redux/slice/queryReasonsSlice
 */

import { createSlice } from "@reduxjs/toolkit";
import { FirestoreService } from "../../api/firebase/firestoreService";
import { FirestoreDocumentReference } from "../../api/firebase/references";
import {
  ReasonModel,
  ReasonsDocumentResponse,
} from "../../data-model/reasonModel";

/** Redux state for query reasons loaded for the active organization. */
export interface QueryReasonsState {
  /** Query reasons ordered for display. */
  data: ReasonModel[];
  /** True while {@link fetchQueryReasons} reads the Firestore document. */
  loading: boolean;
  /** Error recorded when the Firestore request fails. */
  error: string | null;
}

const initialState: QueryReasonsState = {
  data: [],
  loading: false,
  error: null,
};

const queryReasonsSlice = createSlice({
  name: "queryReasons",
  initialState,
  reducers: {
    setQueryReasons: (state, action) => {
      state.data = action.payload;
      state.loading = false;
      state.error = null;
    },
    fetchingQueryReasons: (state) => {
      state.loading = true;
      state.error = null;
      state.data = [];
    },
    setQueryReasonsError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
      state.data = [];
    },
  },
});

export const { setQueryReasons, fetchingQueryReasons, setQueryReasonsError } =
  queryReasonsSlice.actions;

/**
 * Loads query reasons configured for an organization.
 *
 * @param organization - Organization path segment, such as `oncology`.
 * @returns Redux thunk that stores the fetched reasons or error state.
 */
export const fetchQueryReasons =
  (organization: string) => async (dispatch, getState) => {
    const { queryReasons } = getState();
    if (queryReasons.loading) {
      return;
    }

    dispatch(fetchingQueryReasons());
    try {
      const response =
        await FirestoreService.getDocument<ReasonsDocumentResponse>(
          FirestoreDocumentReference.queryReasons(organization),
        );
      dispatch(setQueryReasons(response.reasons));
    } catch (error) {
      dispatch(setQueryReasonsError(error as string));
    }
  };

export default queryReasonsSlice.reducer;
